/**
 * 实例级默认（与后端 TENANT_STOCK_CONFIG / 页面默认标的 对齐）。
 * DEFAULT_STOCK_CODE 仅用于 UI 与部分查询参数，纯净多租户下不作为 X-Tenant-Code 兜底。
 * 构建覆盖：VUE_APP_DEFAULT_STOCK_CODE、VUE_APP_DEFAULT_STOCK_NAME
 */
export const DEFAULT_STOCK_CODE =
  process.env.VUE_APP_DEFAULT_STOCK_CODE || '400001'

export const DEFAULT_STOCK_NAME =
  process.env.VUE_APP_DEFAULT_STOCK_NAME || ''

function _envFlagTrue(v) {
  return ['1', 'true', 'yes'].includes(String(v || '').toLowerCase().trim())
}

/**
 * 与后端 TENANT_HOST_SUFFIXES 对齐；支持逗号分隔多个后缀。
 * 始终合并 dongme.local / dongme.me：避免本地 .env 只写了 dongme.me 时，
 * 访问 400022.dongme.local 解析不到租户码，又开了 VUE_APP_ATTACH_DEV_TENANT_HEADER
 * 导致错误带上 X-Tenant-Code=400167，与域名租户不一致。
 */
function buildTenantHostSuffixesForParse() {
  const fromEnv = (
    (process.env.VUE_APP_TENANT_HOST_SUFFIX || 'dongme.me,dongme.local')
      .toLowerCase()
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  )
  const fallbacks = ['dongme.local', 'dongme.me']
  const seen = new Set()
  const out = []
  for (const s of [...fromEnv, ...fallbacks]) {
    if (s && !seen.has(s)) {
      seen.add(s)
      out.push(s)
    }
  }
  return out
}

const TENANT_HOST_SUFFIXES = buildTenantHostSuffixesForParse()

/**
 * 与后端 extract_tenant_from_host 一致：400001.dongme.me、m.400001.dongme.me → 400001
 */
export function parseTenantCodeFromHostname(hostname) {
  if (!hostname || typeof hostname !== 'string') return null
  const h = hostname.split(':')[0].toLowerCase()
  for (const suffix of TENANT_HOST_SUFFIXES) {
    if (!suffix || !h.endsWith('.' + suffix)) continue
    let head = h.slice(0, -(suffix.length + 1))
    if (head.startsWith('m.')) head = head.slice(2)
    if (/^\d{6}$/.test(head)) return head
  }
  return null
}

/** 开启 VUE_APP_RESOLVE_TENANT_FROM_HOST 时，从当前页域名解析租户代码（供 X-Tenant-Code） */
export function getTenantCodeHeaderFromHostname() {
  if (typeof window === 'undefined') return null
  const v = (process.env.VUE_APP_RESOLVE_TENANT_FROM_HOST || '')
    .toString()
    .toLowerCase()
  if (!['1', 'true', 'yes'].includes(v)) return null
  return parseTenantCodeFromHostname(window.location.hostname)
}

/**
 * 决定 axios / fetch 上的 X-Tenant-Code（纯净多租户：无默认租户兜底）。
 * 1) 当前页 hostname 匹配租户子域时，从域名解析；
 * 2) 仅当 VUE_APP_ATTACH_DEV_TENANT_HEADER=1 且配置了 VUE_APP_DEV_TENANT_CODE 时，在 1) 未命中时附加（供 localhost 显式联调，避免误锁死到历史默认库）。
 */
export function resolveTenantCodeForRequest() {
  if (typeof window !== 'undefined') {
    const fromHost = parseTenantCodeFromHostname(window.location.hostname)
    if (fromHost) return fromHost
  }
  if (!_envFlagTrue(process.env.VUE_APP_ATTACH_DEV_TENANT_HEADER)) {
    return null
  }
  const fixed = (process.env.VUE_APP_DEV_TENANT_CODE || '')
    .toString()
    .trim()
  return /^\d{6}$/.test(fixed) ? fixed : null
}

/**
 * 页内原生 fetch 用请求头：与 main.js 中 axios 拦截器一致（X-Tenant-Code、Cache-Control/Pragma）。
 * @param {Record<string, string>} base 已有头（如 Content-Type、X-CSRFToken）
 */
export function buildFetchApiHeaders(base = {}) {
  const h = { ...base }
  const code = resolveTenantCodeForRequest()
  if (code) {
    h['X-Tenant-Code'] = code
  }
  const cc = h['Cache-Control'] ?? h['cache-control']
  if (cc == null || String(cc).trim() === '') {
    h['Cache-Control'] = 'no-cache'
    h['Pragma'] = 'no-cache'
  }
  return h
}
